/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DELETE_SINGLE_ITEM_SERVICE
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Update deleteBroadcastGroup to accept itemNo for single item deletion.
 *
 * File Size Check:
 * - Current: 259 lines
 * - Adding: ~5 lines
 * - Projected: 264 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastDelete.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DELETE_SERVICE
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Added deleteBroadcastGroup function to support soft-deletion of broadcast groups.
 *
 * File Size Check:
 * - Current: 235 lines
 * - Adding: ~15 lines
 * - Projected: 250 lines
 * - Decision: Proceed (Within 250-line backend limit, borderline for service)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastDelete.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UPLOAD_CONDITION_VALIDATION
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Add condition validation and conditionNeedsReview flag to support manual
 * review of invalid conditions during Excel/CSV upload.
 *
 * File Size Check:
 * - Current: 213 lines
 * - Adding: ~20 lines
 * - Projected: 233 lines
 * - Decision: Proceed (within 250-line backend limit, or 200-line service limit? Rules say 200 for services.)
 *
 * Related Screens / Modules:
 * - client/src/utils/parseBroadcastFile.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DETAILS_API_SERVICE
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Added getBroadcastById function to fetch single broadcast with
 * populated user/company information for broadcast details page.
 *
 * File Size Check:
 * - Current: 187 lines
 * - Adding: ~10 lines
 * - Projected: 197 lines
 * - Decision: Proceed (within 200-line limit)
 *
 * Related Screens / Modules:
 * - client/src/services/broadcastService.ts
 * - client/src/features/broadcast/pages/broadcast-details/BroadcastDetailsPage.tsx
 */
import axios from "axios";
import { apiUrl } from "../config/environment";
import { InventoryCategory, InventoryCondition } from "../interfaces/Inventory";
import { Currency, CURRENCIES } from "../interfaces/Currency";
import { CATEGORY_OPTIONS, CONDITION_OPTIONS } from "../constants/inventoryOptions";
import { FIELD_LABELS, MSG, FieldErrorMap } from "../utils/validationMessages";
import { getAuthHeaders } from "../utils/authUtils";
import { BroadcastGroupListResponse } from "../interfaces/broadcast";

const api: string = `${apiUrl}/api/broadcast-groups`;
const broadcastsApi: string = `${apiUrl}/api/broadcasts`; // [2026-01-28] Phase 5 & 6: Legacy API for actions and responses

export interface BroadcastItemCreate {
  itemNo?: number; // Optional for creation, will be assigned by backend
  manufacturer: string;
  partNumber?: string;
  model?: string;
  quantity: number | undefined;
  condition: InventoryCondition | "";
  price?: number;
  currency: Currency;
  categories?: InventoryCategory[];
  description?: string;
  categoryNeedsReview?: boolean; // Flag for invalid categories that need manual review
  conditionNeedsReview?: boolean; // [2025-01-02] Flag for invalid conditions that need manual review
}

export interface BroadcastGroupCreateData {
  type: "WTB" | "WTS";
  broadcast: BroadcastItemCreate[];
  meta?: { source: "manual" | "xlsx" | "csv"; version?: string };
  // status not sent; server defaults to draft
}

// Legacy alias for backward compatibility
export interface BroadcastCreateData extends BroadcastGroupCreateData {
  items: BroadcastItemCreate[]; // Alias for broadcast field
}

export type ValidationResult = {
  ok: boolean;
  fieldErrors: FieldErrorMap; // per-field messages
  summary?: string; // short item summary for toast
  failingFields?: (keyof FieldErrorMap)[]; // for aggregation
};

export function createBroadcastGroup(payload: BroadcastGroupCreateData) {
  return axios.post(api, payload, getAuthHeaders());
}

// Legacy function for backward compatibility
export function createBroadcast(payload: BroadcastCreateData) {
  // Transform legacy payload to new format
  const transformedPayload: BroadcastGroupCreateData = {
    ...payload,
    broadcast: payload.items || payload.broadcast,
  };
  return axios.post(api, transformedPayload, getAuthHeaders());
}

// Structured validator that returns per-field errors and a concise summary
export function validateBroadcastItem(item: any): ValidationResult {
  const errors: FieldErrorMap = {};

  // manufacturer: required, 2–80
  if (!item.manufacturer) {
    errors.manufacturer = MSG.required(FIELD_LABELS.manufacturer);
  } else if (item.manufacturer.length < 2 || item.manufacturer.length > 80) {
    errors.manufacturer = MSG.lenRange(FIELD_LABELS.manufacturer, 2, 80);
  }

  // partNumber OR model (at least one)
  const hasModel = !!item.model && String(item.model).trim().length > 0;
  const hasPart = !!item.partNumber && String(item.partNumber).trim().length > 0;
  if (!hasModel && !hasPart) {
    const msg = MSG.atLeastOneOf(FIELD_LABELS.partNumber, FIELD_LABELS.model);
    errors.partNumber = msg;
    errors.model = msg;
  }

  // quantity: integer ≥ 1
  const qty = Number(item.quantity);
  if (!Number.isInteger(qty) || qty < 1) {
    errors.quantity = MSG.minInt(FIELD_LABELS.quantity, 1);
  }

  // condition: required enum
  const allowedConditions = [...CONDITION_OPTIONS];
  if (!item.condition) {
    errors.condition = MSG.required(FIELD_LABELS.condition);
  } else if (!allowedConditions.includes(item.condition) && !item.conditionNeedsReview) {
    // [2025-01-02] Allow invalid conditions if flagged for review
    errors.condition = MSG.oneOf(FIELD_LABELS.condition, allowedConditions);
  }

  // currency: required enum
  if (!item.currency) {
    errors.currency = MSG.required(FIELD_LABELS.currency);
  } else if (!CURRENCIES.includes(item.currency)) {
    errors.currency = MSG.oneOf(FIELD_LABELS.currency, CURRENCIES);
  }

  // categories: optional array, max 3, each must be valid enum
  const allowedCategories = [...CATEGORY_OPTIONS];
  if (item.categories !== undefined && item.categories !== null) {
    if (!Array.isArray(item.categories)) {
      errors.categories = "Categories must be an array";
    } else if (item.categories.length > 3) {
      errors.categories = "Maximum 3 categories allowed";
    } else {
      // Validate each category
      const invalidCategories = item.categories.filter(
        (cat: string) => !allowedCategories.includes(cat as any)
      );
      if (invalidCategories.length > 0 && !item.categoryNeedsReview) {
        errors.categories = `Invalid categories: ${invalidCategories.join(", ")}`;
      }
    }
  }

  // price: optional, but if present must be > 0
  if (item.price !== undefined && item.price !== null && item.price !== "") {
    const p = Number(item.price);
    if (!Number.isFinite(p) || p <= 0) {
      errors.price = MSG.positive(FIELD_LABELS.price);
    }
  }

  // description: optional, max 50
  if (item.description && String(item.description).length > 50) {
    errors.description = MSG.lenMax(FIELD_LABELS.description, 50);
  }

  // country: optional, <= 3 chars
  if (item.country && String(item.country).length > 3) {
    errors.country = MSG.lenMax(FIELD_LABELS.country, 3);
  }

  const failingFields = Object.keys(errors) as (keyof FieldErrorMap)[];
  const ok = failingFields.length === 0;

  // Build short summary: "manufacturer, model, price"
  const summary = ok
    ? undefined
    : failingFields
        .slice(0, 3)
        .map((f) => FIELD_LABELS[f])
        .join(", ") + (failingFields.length > 3 ? ` +${failingFields.length - 3} more` : "");

  return { ok, fieldErrors: errors, summary, failingFields };
}

// GET /api/broadcast-groups/global-search - Global search across all broadcasts
export async function getGlobalBroadcasts(params: {
  q?: string;
  page?: number;
  limit?: number;
  type?: "WTB" | "WTS";
  status?: string;
  sort?: "newest" | "oldest";
}): Promise<{ data: BroadcastGroupListResponse }> {
  const searchParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, value.toString());
      }
    });
  }

  const url = searchParams.toString()
    ? `${api}/global-search?${searchParams.toString()}`
    : `${api}/global-search`;

  return axios.get(url, getAuthHeaders()).then((response) => {
    // Map backend response format to BroadcastGroupListResponse format
    const backendData = response.data.data;
    return {
      ...response,
      data: {
        items: backendData.items,
        total: backendData.pagination.totalItems,
        page: backendData.pagination.page,
        pages: backendData.pagination.totalPages,
      },
    };
  });
}

// [2026-07-06] GUEST_ACCESS: anonymous broadcasts search (no auth). Hits the masked
// /api/public route (seller/price stripped server-side per BR-12); surfaces the BR-13 soft wall.
export async function getPublicBroadcastsSearch(params: {
  q?: string;
  page?: number;
  limit?: number;
  type?: "WTB" | "WTS";
  sort?: "newest" | "oldest";
}): Promise<{ data: BroadcastGroupListResponse & { signupRequired?: boolean; signupMessage?: string } }> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, value.toString());
      }
    });
  }
  const publicApi = `${apiUrl}/api/public/broadcasts/search`;
  const url = searchParams.toString() ? `${publicApi}?${searchParams.toString()}` : publicApi;

  // No auth headers — anonymous endpoint.
  return axios.get(url).then((response) => {
    const backendData = response.data.data;
    const pg = backendData.pagination || {};
    return {
      ...response,
      data: {
        items: backendData.items,
        total: pg.total ?? pg.totalItems ?? 0,
        page: pg.page ?? 1,
        pages: pg.pages ?? pg.totalPages ?? 0,
        signupRequired: !!response.data.signupRequired,
        signupMessage: response.data.message || "",
      },
    };
  });
}

// [2025-12-29] Broadcast details: Fetch single broadcast with user info
export async function getBroadcastById(id: string) {
  return axios.get(`${api}/${id}`, getAuthHeaders());
}

// [2025-01-02] Delete single item from broadcast group
export async function deleteBroadcastGroup(id: string, itemNo: number) {
  return axios.delete(`${api}/${id}?itemNo=${itemNo}`, getAuthHeaders());
}

// POST /api/broadcast-groups/:id/send - Send broadcast group (emails async)
export async function sendBroadcastGroup(id: string) {
  return axios.post(`${api}/${id}/send`, {}, getAuthHeaders());
}

// [2026-01-28] Phase 5: Resend broadcast (BR-12)
export async function resendBroadcast(id: string) {
  return axios.post(`${broadcastsApi}/${id}/resend`, {}, getAuthHeaders());
}

// [2026-01-28] Phase 5: Edit broadcast (BR-12)
export async function editBroadcast(
  id: string,
  updates: Partial<BroadcastGroupCreateData>
) {
  return axios.put(`${broadcastsApi}/${id}`, updates, getAuthHeaders());
}

// [2026-01-28] Phase 5: Delete broadcast (BR-12)
export async function deleteBroadcast(id: string) {
  return axios.delete(`${broadcastsApi}/${id}/delete`, getAuthHeaders());
}

// [2026-01-28] Phase 6: Get broadcast for response form (BR-13)
export async function getBroadcastForResponse(id: string) {
  return axios.get(`${broadcastsApi}/${id}/respond`, getAuthHeaders());
}

// [2026-01-28] Phase 6: Submit broadcast response (BR-13)
export async function submitBroadcastResponse(id: string, message: string) {
  return axios.post(
    `${broadcastsApi}/${id}/respond`,
    { message },
    getAuthHeaders()
  );
}
